import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { IdentityPageContent } from '../../../../../../design-system/IdentityPageContent';
import type { AzureAdConfigurationUpdate } from '../../../../../../types/domain/identity-provider/configurations/azureAd';
import { IdentityProviderType } from '../../../../../../types/domain/identity-provider/constants';
import type { IdentityProviderAzure } from '../../../../../../types/domain/identity-provider/identityProvider';
import {
  fetchAzureAdOpenIdMetadata,
  fetchIdentityProvider,
  resetState,
  selectAzureAdConfigError,
  selectAzureAdConfigSubmitting,
  selectAzureAdMetadata,
  selectAzureAdMetadataError,
  selectAzureAdMetadataLoading,
  selectIdentityProvider,
  selectIdentityProviderLoading,
  updateAzureAdConfig,
} from '../../../../../account-detail/security/identity-providers/ducks';
import { selectAccount } from '../../../../../account-detail/shared/ducks';
import { LoginProviderHighlights } from '../shared/LoginProviderHighlights';
import OverviewPanel from '../shared/OverviewPanel';
import { ProviderRouteSkeleton } from '../shared/ProviderRouteSkeleton';
import { ProviderDetailsSection } from './ProviderDetailsSection';

const mapStateToProps = state => ({
  account: selectAccount(state),
  provider: selectIdentityProvider(state),
  loading: selectIdentityProviderLoading(state),
  metadata: selectAzureAdMetadata(state),
  metadataLoading: selectAzureAdMetadataLoading(state),
  metadataError: selectAzureAdMetadataError(state),
  configError: selectAzureAdConfigError(state),
  configSubmitting: selectAzureAdConfigSubmitting(state),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchIdentityProvider,
      fetchAzureAdOpenIdMetadata,
      updateAzureAdConfig,
      resetState,
    },
    dispatch
  ),
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type StateProps = ReturnType<typeof mapStateToProps>;

export type Props = StateProps & DispatchProps;

/**
 * This route allows users to update or add the azure ad open id identity provider configuration
 */
export const AzureProviderRouteComponent: React.FC<Props> = ({
  provider,
  loading,
  metadata,
  metadataLoading,
  metadataError,
  configError,
  configSubmitting,
  actions,
  account,
}) => {
  const canEditProvider = useCheckPermissions(
    [permissionsByEntity.GlobalAccount.FULL, permissionsByEntity.Account.FULL],
    false
  );

  useEffect(() => {
    if (account) {
      actions.fetchAzureAdOpenIdMetadata();
      actions.fetchIdentityProvider({
        identityProviderType: IdentityProviderType.AZURE_AD_OPEN_ID,
      });

      return () => {
        actions.resetState();
      };
    }
  }, [account, actions]);

  if (loading || metadataLoading) {
    return <ProviderRouteSkeleton />;
  }

  if (!account) {
    return null;
  }

  const azureProvider = provider as IdentityProviderAzure | null;

  return (
    <OverviewPanel<AzureAdConfigurationUpdate>
      account={account}
      provider={azureProvider}
      onUpdateConfig={actions.updateAzureAdConfig}
      renderPanel={params => {
        return (
          <IdentityPageContent
            localHighlights={
              <LoginProviderHighlights
                canEdit={canEditProvider}
                isLoading={metadataLoading || configSubmitting}
                providerType={IdentityProviderType.AZURE_AD_OPEN_ID}
                configuredProvider={azureProvider}
                onStatusToggle={provider =>
                  params.onSubmit({ ...provider, enabled: !provider.enabled }, false)
                }
              />
            }
            gridContent={
              <ProviderDetailsSection
                onReset={resetState}
                canEdit={canEditProvider}
                metadata={metadata}
                provider={azureProvider}
                error={configError ?? metadataError}
                isSubmitting={configSubmitting}
                onSubmit={data => params.onSubmit(data, false)}
              />
            }
          />
        );
      }}
    />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(AzureProviderRouteComponent);
